import React, {useEffect, useState} from "react";
import {useGetEnumsQuery} from "../../../../app/globalApi";
import {SuitRowFormatting, SuitsHead, taxInputRow} from "../../../../helpers/utils/OrderDetailUtils";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import CommonSelectorModal from "../../../../components/common/form/CommonSelectorModal";
import {DataTableColumn} from "../../../../components/common/datatable/DataTableTypes";
import {useSearchCollectorQuery} from "../../../data/collector/collector";
import {FaMagnifyingGlass} from "react-icons/fa6";
import {useSearchTaxSuitCollectorQuery} from "../../ordersApi";

interface TaxSuit {
    payeeId: string;
    payee: string;
    suitNumber: string;
    parcel: string;
    feeType: string;
    amount: number | null;
    balance: number | null;
    collector: any
}

interface Account {
    taxSuits: TaxSuit[];

    [key: string]: any;
}

interface Certificate {
    [key: string]: any;
}

interface Props {
    curAccount: Account;
    setIsSavedResult: (value: boolean) => void;
    certificate: Certificate;
    setCertificate: React.Dispatch<React.SetStateAction<Certificate>>;
    showAccount: number;
    editable: boolean;
    setUpdatedAccountIndexList: any;
}

export function TaxSuitsSection({
                                    curAccount,
                                    setIsSavedResult,
                                    certificate,
                                    setCertificate,
                                    showAccount,
                                    editable,
                                    setUpdatedAccountIndexList
                                }: Props): JSX.Element {

    const [addedNewSuit, setAddedNewSuit] = useState<boolean>(false);
    const [removeSuit, setRemoveSuit] = useState<boolean>(false);
    const [suitId, setSuitId] = useState<number>(0);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [msg, setMsg] = useState<string>("");
    const {data: SuitsFeeTypeEnums} = useGetEnumsQuery("");
    const [showCollectorModal, setShowCollectorModal] = useState<boolean>(false);
    const [clearSuitRow, setClearSuitRow] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<any>({});

    useEffect(() => {
        if (addedNewSuit || removeSuit || clearSuitRow || suitId) {
            setUpdatedAccountIndexList((prev: number[]) =>
                prev.includes(showAccount) ? prev : [...prev, showAccount]
            );
            setIsSavedResult(false);
        }
    }, [addedNewSuit, removeSuit, clearSuitRow, suitId]);


    useEffect(() => {
        if (clearSuitRow) {
            curAccount.taxSuits[suitId].payee = "";
            setCertificate(certificate => certificate);
            setClearSuitRow(false);
        }
    }, [clearSuitRow]);


    useEffect(() => {
        if (removeSuit && certificate) {
            const account = certificate.accounts[showAccount]; // Access the account from certificate

            if (account.taxSuits.length === 1) {
                account.taxSuits = [{
                    payeeId: "",
                    payee: "",
                    suitNumber: "",
                    parcel: "",
                    feeType: "",
                    amount: null,
                    balance: null,
                    collector: null,
                }];
            } else {
                account.taxSuits = account.taxSuits.filter((_: any, p: any) => p !== suitId);
            }

            // Update the certificate state
            setCertificate((prevCertificate) => {
                const updatedCertificate = structuredClone(prevCertificate);
                updatedCertificate.accounts[showAccount] = account;
                return updatedCertificate;
            });

            setRemoveSuit(false);
        }
    }, [removeSuit, certificate, showAccount, suitId]);


    useEffect(() => {
        if (addedNewSuit && certificate) {
            const account = certificate.accounts[showAccount]; // Access the account from certificate
            let tempPer = account?.taxSuits?.some((el: any) => {
                return el.payee === '' || el.suitNumber === '' || el.parcel === '' || el.feeType === '';
            });

            if (!tempPer) {
                setCertificate((prevCertificate) => {
                    const newCertificate = structuredClone(prevCertificate);
                    newCertificate.accounts[showAccount].taxSuits = [
                        ...newCertificate.accounts[showAccount].taxSuits,
                        {
                            payeeId: "",
                            payee: "",
                            suitNumber: "",
                            parcel: "",
                            feeType: "",
                            amount: null,
                            balance: null,
                        },
                    ];

                    return newCertificate;
                });

                setAddedNewSuit(false);
            } else {
                setMsg("Payee, Suit Number, Parcel and Fee Type are required in Tax Suits");
                setShowConfirmation(true);
                setAddedNewSuit(false);
            }
        }
    }, [addedNewSuit]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedRow && selectedRow?.name) {
            const updatedCertificate = structuredClone(certificate);
            updatedCertificate.accounts[showAccount].taxSuits[suitId].payee = selectedRow?.name;
            updatedCertificate.accounts[showAccount].taxSuits[suitId].payeeId = selectedRow?.id;
            setCertificate(updatedCertificate);

            setUpdatedAccountIndexList((prev: number[]) =>
                prev.includes(showAccount) ? prev : [...prev, showAccount]
            );

            setIsSavedResult(false);
        }
        setShowCollectorModal(false);
    };


    const columns: DataTableColumn[] = [
        {
            label: "Name",
            accessor: "name",
            filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''}/>
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],
        },
        {label: "State", accessor: "state.stateAbbreviation"},
        {label: "County", accessor: "countyName"}
    ]

    return (
        <>
            <div className="col-lg-12 col-sm-12 border border-highlight-3 mb-3 mt-3">
                <div className="border border-highlight-3 bg-highlight-5 py-2 text-text-0 pl-2"><b>Tax Suits</b></div>
                <div>
                    <table className="w-full">
                        {SuitsHead(setAddedNewSuit, editable)}
                        <tbody>
                        {curAccount?.taxSuits?.map((row, t) => (
                            <tr  className={"border-b border-surface-5"} key={t}>
                                <td>
                                    <i
                                        onClick={() => {
                                            if (editable) {
                                                setSuitId(t);
                                                setRemoveSuit(true);
                                            }
                                        }}
                                        className="fa fa-icon-color fa-trash onHover text-highlight-0 ml-0.5"
                                        title="Delete Tax Suit"
                                    />

                                </td>

                                <td className="w-60">
                                    <div className="relative">
                                            <span
                                                title={row?.payee}
                                                className="bg-text-0 radius-md p-3 text-surface-0 w-full block form-control"
                                                data-placeholder="Select Collector ..."
                                            >{row?.payee || "Select Collector ..."}</span>
                                        {!row.payee &&
                                            <span

                                                onClick={() => {
                                                    if (editable) {
                                                        setSuitId(t);
                                                        setShowCollectorModal(true);
                                                    }
                                                }}
                                                className="fa fa-search ml-xl-n4 collector-account-modal text-highlight-5 top-[12px]  right-[15px] absolute"
                                            />
                                        }
                                        {row.payee &&
                                            <span
                                                onClick={() => {
                                                    if (editable) {
                                                        setSuitId(t);
                                                        setClearSuitRow(true);
                                                    }
                                                }}
                                                className="fa fa-times ml-xl-n4 text-highlight-5 -translate-y-1/2 top-1/2  right-[15px] absolute"
                                            />
                                        }
                                    </div>
                                </td>
                                <td>{taxInputRow(row, 'suitNumber', setIsSavedResult, setCertificate, editable, showAccount, setUpdatedAccountIndexList, t)}</td>
                                <td>{taxInputRow(row, 'parcel', setIsSavedResult, setCertificate, editable, showAccount, setUpdatedAccountIndexList, t)}</td>
                                <td>
                                    <select
                                        className="border-0 form-control text-center"
                                        name="feeType"
                                        value={row.feeType}
                                        onChange={(e) => {
                                            if (editable) {
                                                setCertificate((prevCertificate: any) => {
                                                    const updatedCertificate = { ...prevCertificate };
                                                    updatedCertificate.accounts[showAccount].taxSuits[t].feeType = e.target.value;
                                                    return updatedCertificate;
                                                });

                                                setIsSavedResult(false);
                                                setUpdatedAccountIndexList((prev: number[]) =>
                                                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                                                );
                                            }
                                        }}
                                        disabled={!editable || row.payee === ''}
                                    >
                                        <option value="">Select Fee Type...</option>
                                        {SuitsFeeTypeEnums?.["SuitsFeeType"].map((curEnum: {
                                            constant: string;
                                            value: string
                                        }) => (
                                            <option key={curEnum.constant} value={curEnum.constant}>
                                                {curEnum.value}
                                            </option>
                                        ))}
                                    </select>


                                </td>
                                <td className="text-center">
                                    {SuitRowFormatting(
                                        row,
                                        setCertificate,
                                        setSuitId,
                                        t,
                                        certificate,
                                        "amount",
                                        setIsSavedResult,
                                        editable,
                                        showAccount,
                                        setUpdatedAccountIndexList
                                    )}
                                </td>
                                <td className="text-center">
                                    {SuitRowFormatting(
                                        row,
                                        setCertificate,
                                        setSuitId,
                                        t,
                                        certificate,
                                        "balance",
                                        setIsSavedResult,
                                        editable,
                                        showAccount,
                                        setUpdatedAccountIndexList
                                    )}
                                </td>

                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showConfirmation && (
                <ConfirmationModal
                    title="Alert"
                    bodyMessage={msg}
                    show={showConfirmation}
                    setShow={setShowConfirmation}
                />
            )}
            {showCollectorModal &&
                <CommonSelectorModal
                    title="Collector Selector"
                    columns={columns}
                    data={useSearchTaxSuitCollectorQuery}
                    show={showCollectorModal}
                    onSubmit={handleSubmit}
                    setShow={setShowCollectorModal}
                    setSelectedRow={setSelectedRow}
                />
            }
        </>
    );
}